<script lang="ts">
	import type { TooltipOptions } from '$lib/actions/tooltip/types';
	import type { IconPosition } from '$lib/components/atoms/iconlabel/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import type { ButtonColor, ButtonSize, ButtonVariant } from './types';
	import { tooltip as tooltipAction } from '$lib/actions/tooltip/tooltip';
	import IconLabel from '$lib/components/atoms/iconlabel/IconLabel.svelte';

	interface Props {
		variant?: ButtonVariant;
		color?: ButtonColor;
		size?: ButtonSize;
		text?: string;
		icon?: IconName;
		iconPosition?: IconPosition;
		disabled?: boolean;
		tooltip?: TooltipOptions;
		ariaLabel?: string;
		ariaHidden?: boolean;
		hideTextOnMobile?: boolean;
		iconClasses?: ClassValue;
		labelClasses?: ClassValue;
		mobileColor?: ButtonColor;
		noMinWidth?: boolean;
		class?: ClassValue;
		onclick?: (event: MouseEvent) => void;
		onmousedown?: (event: MouseEvent) => void;
		onpointerdown?: (event: PointerEvent) => void;
		children?: Snippet;
		badge?: Snippet;
		dataTestId?: string;
	}

	const {
		variant = 'filled',
		color = 'primary',
		size = 'md',
		text = undefined,
		icon = undefined,
		iconPosition = 'left',
		disabled = false,
		tooltip = undefined,
		ariaLabel = undefined,
		ariaHidden = undefined,
		hideTextOnMobile = false,
		iconClasses = undefined,
		labelClasses,
		mobileColor = undefined,
		noMinWidth = false,
		class: classes,
		onclick,
		onmousedown,
		onpointerdown,
		children,
		badge,
		dataTestId,
	}: Props = $props();

	const colorClasses = $derived.by(() => {
		return mobileColor && mobileColor !== color
			? `btn-${variant}-${mobileColor ?? color} md:btn-${variant}-${color}`
			: `btn-${variant}-${color}`;
	});

	const minWidthMax = $derived(noMinWidth ? undefined : !children);

	const labelSpacingClasses = $derived.by(() => {
		if (text && icon) {
			if (iconPosition === 'left') {
				return 'ml-1';
			} else {
				return 'mr-1';
			}
		} else {
			return '';
		}
	});
</script>

<button
	type="button"
	aria-label={ariaLabel}
	aria-hidden={ariaHidden}
	class={[
		colorClasses,
		`btn-${size}`,
		classes,
		{
			'min-w-max': minWidthMax,
		},
	]}
	{disabled}
	{onclick}
	{onmousedown}
	{onpointerdown}
	use:tooltipAction={tooltip}
	data-testid={dataTestId}
>
	{#if children}
		{@render children()}
	{:else}
		<IconLabel
			{icon}
			label={text}
			{iconPosition}
			hideLabelOnMobile={hideTextOnMobile}
			labelClasses={[labelSpacingClasses, labelClasses]}
			{iconClasses}
			tag="span"
			iconTag="span"
		/>
	{/if}
	{#if badge}
		{@render badge()}
	{/if}
</button>
